import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';

// Layout & Auth
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';

// Pages
import DailyStackView from './components/views/DailyStackView';
import BrandsList from './pages/BrandList'; 
import BrandDetail from './pages/BrandDetail';
import UsersList from './pages/UsersList'; 
import UserDetail from './pages/UserDetail';
import CreateTaskPage from './pages/ScheduleTask';
import ClientList from './pages/ClientList'; 
import ClientDetail from './pages/ClientDetail';

// Settings Pages
import PostTypesSettings from './pages/PostTypesSettings';
import BudgetRangesSettings from './pages/BudgetRangesSettings';

// API Services
import taskService from './api/taskService';

/**
 * AppContent handles the routing and main logic.
 * It receives isDark and setIsDark as props from the parent.
 */
function AppContent({ isDark, setIsDark }) {
  const location = useLocation();
  const [activeFilters, setActiveFilters] = useState({});
  
  // --- Global Sidebar/Task States ---
  const [selectedTask, setSelectedTask] = useState(null);
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(false);
  
  // Data States
  const [tasks, setTasks] = useState([]); 
  const [entities, setEntities] = useState([]); 
  const [enquiries, setEnquiries] = useState([]); 
  const [postTypes, setPostTypes] = useState([]);
  const [budgetRanges, setBudgetRanges] = useState([]);
  const [allUsers, setAllUsers] = useState([]); 

  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });

  // --- GLOBAL TASK HANDLERS ---
  const handleTaskSelect = useCallback((task) => {
    const taskData = task.extendedProps ? { id: task.id, ...task.extendedProps } : task;
    setSelectedTask(taskData);
    setIsRightSidebarOpen(true);
  }, []);

  const handleSaveChanges = useCallback(async (updatedPost) => {
    const loadingToast = toast.loading("UPDATING TASK...");
    try {
      await taskService.updateTask(updatedPost.id, updatedPost);
      
      // Update local state manually so the calendar/list reflects the change
      setTasks(prev => prev.map(t => t.id === updatedPost.id ? updatedPost : t));
      
      toast.success("DATABASE UPDATED SUCCESSFULLY", { id: loadingToast });
      setIsRightSidebarOpen(false);
    } catch (err) {
      console.error("Global Save error:", err);
      toast.error("FAILED TO UPDATE DATABASE", { id: loadingToast });
    }
  }, []);

  const handleDeleteTask = useCallback(async (taskId) => {
    // 1. Show the loading toast and capture the ID
    const loadingToast = toast.loading("DELETING TASK...");
    
    try {
      await taskService.deleteTask(taskId);
      
      // 2. Remove the task from local state immediately
      setTasks(prev => prev.filter(t => t.id !== taskId));
      
      // 3. Resolve the toast using the ID
      toast.success("TASK DELETED SUCCESSFULLY", { id: loadingToast });
      
      // 4. Close the sidebar without a reload
      setIsRightSidebarOpen(false);
    } catch (err) {
      console.error("Global Delete error:", err);
      toast.error("FAILED TO DELETE TASK", { id: loadingToast });
    }
  }, []);

  const handleDataSync = useCallback((data) => setTasks(data), []);
  const handleEntitiesSync = useCallback((data) => setEntities(data), []);
  const handleEnquiriesSync = useCallback((data) => setEnquiries(data), []);
  const handlePostTypesSync = useCallback((data) => setPostTypes(data), []);
  const handleBudgetsSync = useCallback((data) => setBudgetRanges(data), []);
  const handleUsersSync = useCallback((data) => setAllUsers(data), []); 

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    toast.success(`WELCOME BACK, ${userData.name?.toUpperCase()}`);
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
    toast.success("LOGGED OUT");
  };

  const getSidebarData = () => {
    const path = location.pathname;
    // When on the schedule/home page, we need the tasks
    if (path === '/' || path === '') return tasks;
    
    if (path.startsWith('/users')) return allUsers; 
    if (path.startsWith('/brands')) {
      return entities.map(e => ({
        ...e,
        // Ensure the filter sidebar can see "Active" status
        is_active: e.is_active == 1 ? "Active" : "Inactive"
      }));
    }
    if (path.startsWith('/enquiry')) return enquiries; 
    if (path.startsWith('/settings/post-types')) return postTypes;
    if (path.startsWith('/settings/budget-ranges')) return budgetRanges;
    
    return tasks; 
  };

  return (
    <div className="App min-h-screen bg-white dark:bg-slate-900 transition-colors duration-300">
      <Routes>
        <Route 
          path="/login" 
          element={!user ? <Login isDark={isDark} setIsDark={setIsDark} onLogin={handleLogin} /> : <Navigate to="/" />} 
        />

        <Route 
          path="/" 
          element={
            user ? (
              <Dashboard 
                isDark={isDark} 
                setIsDark={setIsDark} 
                user={user} 
                onLogout={handleLogout} 
                onFilterChange={setActiveFilters} 
                rawData={getSidebarData()}
                activeFilters={activeFilters}
                onTaskClick={handleTaskSelect}
                onSave={handleSaveChanges}
                onDelete={handleDeleteTask}
                selectedTask={selectedTask}
                isRightSidebarOpen={isRightSidebarOpen}
                setIsRightSidebarOpen={setIsRightSidebarOpen}
              />
            ) : (
              <Navigate to="/login" />
            )
          }
        >
          <Route 
            index 
            element={
              <div className="pl-6 flex-1">
                <DailyStackView />
              </div>
            } 
          />

          <Route path="create-task" element={<CreateTaskPage isDark={isDark} />} />
          <Route path="users" element={<UsersList isDark={isDark} activeFilters={activeFilters} onDataLoaded={handleUsersSync} />} />
          <Route path="users/:id" element={<UserDetail isDark={isDark} />} />
          <Route path="users/new" element={<UserDetail isCreateMode={true} isDark={isDark} />} />
          <Route path="brands" element={<BrandsList isDark={isDark} activeFilters={activeFilters} onDataLoaded={handleEntitiesSync} />} />
          <Route path="brands/:id" element={<BrandDetail isDark={isDark} />} />
          <Route path="enquiry" element={<ClientList isDark={isDark} activeFilters={activeFilters} onDataLoaded={handleEnquiriesSync} />} />
          <Route path="enquiry/:id" element={<ClientDetail isDark={isDark} />} />
          
          <Route path="settings">
            <Route path="post-types" element={<PostTypesSettings isDark={isDark} activeFilters={activeFilters} onDataLoaded={handlePostTypesSync} />} />
            <Route path="budget-ranges" element={<BudgetRangesSettings isDark={isDark} activeFilters={activeFilters} onDataLoaded={handleBudgetsSync} />} />
          </Route>
        </Route>
      </Routes>
    </div>
  );
}

/**
 * Root App component.
 * We mount Toaster here so it's ready before any routes or sub-effects run.
 */
export default function App() {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  return (
    <>
      {/* Move Toaster OUTSIDE BrowserRouter and remove position: relative */}
      <Toaster 
        position="top-center"
        reverseOrder={false}
        containerStyle={{
          zIndex: 9999999,
        }}
        toastOptions={{
          style: {
            // REMOVED position: 'relative' - this was the likely culprit for the "missing" toast
            background: isDark ? '#0f172a' : '#ffffff', 
            color: isDark ? '#cbd5e1' : '#475569', 
            border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '600',
            padding: '12px 20px',
            textTransform: 'uppercase',
            letterSpacing: '0.1em'
          },
        }}
      />

      <BrowserRouter>
        <AppContent isDark={isDark} setIsDark={setIsDark} />
      </BrowserRouter>
    </>
  );
}